import {Question as QuestionType, QuestionTypeSlug} from "@/Types/question";

export default class Question {
    question: QuestionType;

    constructor(question: QuestionType) {
        this.question = question;
    }

    get(key: string) {
        return this.question?.[key as keyof QuestionType];
    }

    get title(): string {
        return this.question?.title;
    }

    get id(): string {
        return this.question?.id;
    }

    get type() {
        return this.question?.type;
    }

    get stimuli() {
        return this.question?.stimuli ?? [];
    }

    get user() {
        return this.question?.user;
    }

    get pageCount() {
        return +this.question?.stimuli?.[0]?.files?.length;
    }

    get stimulus() {
        return this.question?.stimuli?.[0] ?? null;
    }

    get options() {
        return this.question?.options ?? [];
    }

    get categories() {
        return this.question?.categories ?? [];
    }

    get responses(): Array<any> {
        return this.question?.responses ?? [];
    }

    get showResponses(): boolean {
        return !!(this.param("show_responses") || this.group?.is_owner);
    }

    get group() {
        return this.question?.group ?? null;
    }

    get params(): object {
        return this.question?.params ?? {};
    }

    get comments(): object {
        return this.question?.comments ?? [];
    }

    get cover_image(): object {
        return this.question?.cover_image;
    }

    get isPublished(): boolean {
        return this.question?.is_published;
    }

    get isArchived(): boolean {
        return !!this.question?.deleted_at;
    }

    isOwner(user: any): boolean {
        return this.group.user_id === user.id;
    }

    param(key: string, value?: any): any {
        if (value !== undefined) {
            this.question.params[key] = value;
            return this;
        }
        return this.params[key] ?? false;
    }

    response(response: string): any {
        return this.question?.responses?.find((A) => A.id === response);
    }

    comment(comment: string): any {
        return this.question?.comments?.find((C) => C.id === comment);
    }

    setResponses(responses: any): any {
        this.question.responses = responses;
        return this;
    }

    addResponse(response: any): any {
        this.question?.responses?.unshift(response);
        return this;
    }

    setComments(comments: any): any {
        this.question.comments = comments;
        this.question.comments_count = comments.length;
        return this;
    }

    addComment(comment: any): any {
        this.question?.comments.unshift(comment);
        this.question.comments_count = this.question?.comments.length;
        return this;
    }

    removeComment(comment: string): any {
        this.question.comments = this.question?.comments.filter((C) => C.id !== comment);
        this.question.comments_count = this.question?.comments.length;
        return this;
    }

    option(option: number): any {
        return this.question?.options.find((O) => O.id === option);
    }

    get slug(): QuestionTypeSlug | "" {
        const type = this.question?.type;
        if (type && typeof type === "object") {
            return type.slug ?? "";
        }
        return (type as QuestionTypeSlug) ?? "";
    }

    is(type: QuestionTypeSlug | QuestionTypeSlug[]): boolean {
        const slug = this.slug;
        if (!slug) {
            return false;
        }

        if (Array.isArray(type)) {
            return type.includes(slug);
        }

        return slug === type;
    }

    published(fromto: string): boolean {
        return;
    }

    get hasCoverImage(): boolean {
        return !!(
            (this.param("cover_image_type") === "custom" && this.question?.cover_image) ||
            this.param("cover_image_type") === "stimulus"
        );
    }
}
